import { useIntl } from 'react-intl';
import { emphasisSuppressionMessages } from './emphasis-suppression.messages';
import { useCopySettings } from './useCopySettings';
import { type CopySettings } from './useCopySettings.types';

/** Shared predicate for every display/affordance/shortcut suppression path. */
export const isEmphasisSuppressed = (
  settings: Pick<CopySettings, 'boldAndItalics'>,
  isImprovedCopyEnabled: boolean
): boolean => isImprovedCopyEnabled && !settings.boldAndItalics;

/**
 * True when the "Bold & italics" copy setting must be reflected in the note
 * editor display: the improved-copy cohort has the toggle off, so
 * bold/italic/underline stop rendering and their toolbar affordances disable.
 * Mirrors the bullets-off pattern (`plainListMarkers`) — display only, the
 * stored note keeps its marks and copy/export behaviour is unchanged
 * (`stripBoldAndItalics` already unwraps the same `strong`/`b`/`em`/`i`/`u`
 * group).
 */
export const useEmphasisSuppressed = (): boolean => {
  const { settings, isImprovedCopyEnabled } = useCopySettings();
  return isEmphasisSuppressed(settings, isImprovedCopyEnabled);
};

/**
 * True when a note-scoped surface's bold/italic/underline affordances should
 * disable: "Bold & italics" is off (see {@link useEmphasisSuppressed}) AND the
 * surface sits in the NOTE editor. Document/template/context editors keep
 * their buttons live — the display suppression only applies to the note tab.
 */
export const useEmphasisDisabled = (isNoteEditor: boolean): boolean =>
  useEmphasisSuppressed() && isNoteEditor;

/**
 * The instructive tooltip for a disabled Bold/Italic/Underline toolbar
 * button: names the cause ("Bold & italics" is off) and the fix (turn it on
 * in copy settings). Shown in place of the button's normal label + shortcut,
 * which would advertise an action the button can't currently perform.
 */
export const useEmphasisDisabledTooltip = (): string => {
  const intl = useIntl();
  return intl.formatMessage(emphasisSuppressionMessages.disabledTooltip);
};
